import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { templatesAPI, favoritesAPI, ConsentTemplate } from '../services/api';
import { 
  FileText, 
  Search,
  ArrowLeft,
  Loader,
  AlertCircle,
  CheckCircle,
  Star
} from 'lucide-react';

const ConsentFiller: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<ConsentTemplate[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTemplates();
    loadFavorites();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await templatesAPI.getAll(true);
      setTemplates(data);
    } catch (err: any) {
      setError('Error al cargar plantillas: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const ids = await favoritesAPI.getIds();
      setFavoriteIds(ids);
    } catch {
      setFavoriteIds([]);
    }
  };

  const toggleFavorite = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    try {
      if (favoriteIds.includes(templateId)) {
        await favoritesAPI.remove(templateId);
        setFavoriteIds((prev) => prev.filter((id) => id !== templateId));
      } else {
        await favoritesAPI.add(templateId);
        setFavoriteIds((prev) => [...prev, templateId]);
      }
    } catch {
      // ignore
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.procedure_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const favoriteTemplates = filteredTemplates.filter((t) => t.id && favoriteIds.includes(t.id));
  const otherTemplates = filteredTemplates.filter((t) => !t.id || !favoriteIds.includes(t.id));

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando plantillas…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-6xl mx-auto">
        <div className="page-hero mb-2">
          <button
            type="button"
            onClick={() => navigate('/templates')}
            className="btn-secondary mb-4 py-2 text-sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a plantillas
          </button>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
            Flujo clínico
          </p>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Llenar consentimientos
          </h1>
          <p className="max-w-2xl text-sm text-slate-600 sm:text-base">
            Elija una plantilla para completar el consentimiento informado con el paciente.
          </p>
        </div>

        {/* Barra de búsqueda */}
        <div className="card mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por título del consentimiento o procedimiento..."
              className="input-field pl-12"
            />
          </div>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de plantillas */}
        {filteredTemplates.length === 0 ? (
          <div className="card text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchTerm ? 'No se encontraron plantillas' : 'No hay plantillas disponibles'}
            </h3>
            <p className="text-gray-500">
              {searchTerm 
                ? 'Intenta con otro término de búsqueda'
                : 'Crea una plantilla primero para poder llenar consentimientos'}
            </p>
          </div>
        ) : (
          <>
            {favoriteTemplates.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  Mis plantillas favoritas
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favoriteTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer border-amber-200 bg-amber-50/50"
                      onClick={() => navigate(`/consents/fill/${template.id}`)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                            {template.title}
                          </h3>
                          {template.procedure_title && (
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-semibold">Procedimiento:</span> {template.procedure_title}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => toggleFavorite(e, template.id!)}
                          className="p-1 rounded hover:bg-amber-100 flex-shrink-0"
                          title="Quitar de favoritos"
                        >
                          <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-200">
                        <span>Código: {template.code}</span>
                        <span>Versión: {template.version}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {otherTemplates.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  {favoriteTemplates.length > 0 ? 'Otras plantillas' : 'Plantillas'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {otherTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                      onClick={() => navigate(`/consents/fill/${template.id}`)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                            {template.title}
                          </h3>
                          {template.procedure_title && (
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-semibold">Procedimiento:</span> {template.procedure_title}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => toggleFavorite(e, template.id!)}
                          className="p-1 rounded hover:bg-gray-100 flex-shrink-0 text-gray-400 hover:text-amber-500"
                          title="Añadir a favoritos"
                        >
                          <Star className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-200">
                        <span>Código: {template.code}</span>
                        <span>Versión: {template.version}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConsentFiller;
